/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { DialogWithForm } from '@strata/react-dialog';
import { TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { DetailLabel } from '@strata/react-label-value-pairs';
import {
    ACCEPTED_QUESTION_ID,
    BenefitAttributeAutoPopulateOptions,
    ComponentTypes,
} from '@strata/react-question-tree';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { validationStrings } from '@strata/strata-ui-strings';
import { FieldArray, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { useGet } from 'services/Http';

const BenefitAttributeAutoPopulateOptionHelperTextMap = {
    [BenefitAttributeAutoPopulateOptions.ANY_OTHER_VALUE]:
        'Auto selects option if no previous criteria is met for the Attribute Type',
};

const isOptionsComponent = (component) =>
    component === ComponentTypes.MULTI_SELECT || component === ComponentTypes.RADIO;

const mapExistingQuestionForDialog = (question) => ({
    questionText: question?.question_text?.replaceAll(`<br />`, '\n'),
    component: question?.component,
    options: question?.options?.map((option) => option.answer) ?? [],
    benefitAutoPopulate:
        question?.benefit_auto_populate?.map((condition) => ({
            attribute_type: condition.attribute_type,
            attribute_value: condition.attribute_value,
            option: question?.options?.find((option) => option.option_id === condition.option_id)
                ?.answer,
        })) ?? [],
});

export const QuestionDialog = ({
    question = undefined,
    existingQuestionList = undefined,
    payerId,
    showAutoAccept = false,
    isOpen,
    onSubmit,
    onSecondaryButtonClick,
}) => {
    const existingQuestionOptions = useMemo(
        () =>
            existingQuestionList
                ?.filter((q) => q.question_id !== question?.question_id)
                .map((q) => ({
                    name: `${q.question_id}. ${q.question_text?.replaceAll(`<br />`, '\n')}`,
                    ...mapExistingQuestionForDialog(q),
                })),
        [existingQuestionList, question],
    );

    const componentOptions = useMemo(
        () => [
            ...(showAutoAccept ? [{ name: 'Auto Accept', value: ComponentTypes.AUTO_ACCEPT }] : []),
            { name: 'Checkbox', value: ComponentTypes.MULTI_SELECT },
            { name: 'Numeric Input', value: ComponentTypes.NUMBER_INPUT },
            { name: 'Radio Button', value: ComponentTypes.RADIO },
            { name: 'Text Area', value: ComponentTypes.TEXT_AREA },
            { name: 'Text Input', value: ComponentTypes.TEXT_INPUT },
        ],
        [showAutoAccept],
    );

    const benefitAttributeTypes = useGet(
        isOpen ? `/clients/admin/${payerId}/benefits/attribute_types` : null,
    );
    const benefitAttributeTypeOptions = useMemo(
        () => benefitAttributeTypes?.map((attribute) => attribute.attribute_type),
        [benefitAttributeTypes],
    );

    return (
        <Formik
            initialValues={mapExistingQuestionForDialog(question)}
            enableReinitialize
            validationSchema={Yup.object({
                questionText: Yup.string().when('component', {
                    is: !ComponentTypes.AUTO_ACCEPT,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                component: Yup.string().required(validationStrings.requiredField.errorMessage),
                options: Yup.array().when('component', {
                    is: (component) => isOptionsComponent(component),
                    then: (schema) =>
                        schema
                            .of(Yup.string().required(validationStrings.requiredField.errorMessage))
                            .min(1),
                }),
                benefitAutoPopulate: Yup.array().when('component', {
                    is: (component) => component === ComponentTypes.RADIO,
                    then: (schema) =>
                        schema.of(
                            Yup.object({
                                attribute_type: Yup.string().required(
                                    validationStrings.requiredField.errorMessage,
                                ),
                                attribute_value: Yup.string().required(
                                    validationStrings.requiredField.errorMessage,
                                ),
                            }),
                        ),
                }),
            })}
            onSubmit={async (values, { resetForm }) => {
                const { component } = values;
                const options = isOptionsComponent(component)
                    ? values.options.map((option, index) => ({
                          option_id: index + 1,
                          answer: option,
                      }))
                    : [];
                const benefitAutoPopulate =
                    component === ComponentTypes.RADIO
                        ? values.benefitAutoPopulate.map((condition) => ({
                              attribute_type: condition.attribute_type,
                              attribute_value: condition.attribute_value,
                              option_id: options.find(
                                  (option) => option.answer === condition.option,
                              )?.option_id,
                          }))
                        : [];

                const newQuestion = {
                    ...question,
                    component,
                    question_text:
                        component !== ComponentTypes.AUTO_ACCEPT
                            ? values.questionText.replaceAll('\n', `<br />`)
                            : 'Auto Accept',
                    options,
                    option_validation:
                        component !== question.component
                            ? [
                                  {
                                      rule: '{}',
                                      // Auto-populate question_id to ACCEPTED_QUESTION_ID if Auto accept policy
                                      question_id:
                                          component === ComponentTypes.AUTO_ACCEPT
                                              ? ACCEPTED_QUESTION_ID
                                              : undefined,
                                  },
                              ]
                            : question.option_validation,
                    benefit_auto_populate: benefitAutoPopulate,
                };

                try {
                    await onSubmit(newQuestion);
                    resetForm();
                    onSecondaryButtonClick();
                } catch (err) {
                    handleError('Error updating question.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={`Question ${question?.question_id}`}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        formik.resetForm();
                        onSecondaryButtonClick();
                    }}
                    primaryButtonLabel="Save"
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    width={1500}
                    dialogContent={
                        <>
                            {existingQuestionOptions?.length > 0 && (
                                <SuggestSingleSelectFormik
                                    id="existingQuestion"
                                    label="Populate With Existing Question"
                                    values={existingQuestionOptions}
                                    onChange={(newValue) =>
                                        formik.setValues({
                                            existingQuestion: newValue,
                                            questionText: newValue?.questionText,
                                            component: newValue?.component,
                                            options: newValue?.options ?? [],
                                            benefitAutoPopulate:
                                                newValue?.benefitAutoPopulate ?? [],
                                        })
                                    }
                                />
                            )}
                            {formik.values.component !== ComponentTypes.AUTO_ACCEPT && (
                                <TextAreaFormik id="questionText" label="Question Text" required />
                            )}
                            <SuggestSingleSelectFormik
                                id="component"
                                label="Component"
                                required
                                helperText={
                                    question?.component
                                        ? 'Note: Changing a component for an existing question will reset the logic for that question.'
                                        : undefined
                                }
                                values={componentOptions}
                                selectedItem={componentOptions.find(
                                    (component) => formik.values.component === component.value,
                                )}
                                onChange={(newValue) =>
                                    formik.setValues({
                                        ...formik.values,
                                        component: newValue?.value,
                                        options:
                                            isOptionsComponent(newValue?.value) &&
                                            formik.values.options.length === 0
                                                ? ['']
                                                : formik.values.options,
                                    })
                                }
                            />
                            {isOptionsComponent(formik.values.component) && (
                                <FieldArray name="options">
                                    {(arrayHelpers) => (
                                        <div className="margin__bottom--large">
                                            {formik.values.options.map((_option, index) => (
                                                <div
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    key={index}
                                                    style={{ display: 'flex' }}
                                                >
                                                    <div
                                                        className={
                                                            index === 0
                                                                ? 'padding__right--large'
                                                                : 'padding__right--small'
                                                        }
                                                        style={{ flexGrow: '1' }}
                                                    >
                                                        <TextInputFormik
                                                            id={`${arrayHelpers.name}.${index}`}
                                                            label={`Option ${index + 1}`}
                                                            required
                                                        />
                                                    </div>
                                                    {index > 0 && (
                                                        <Icon
                                                            className="margin__top--large"
                                                            title="Remove"
                                                            icon={IconNames.CROSS}
                                                            onClick={() =>
                                                                arrayHelpers.remove(index)
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                            <ActionButtonText
                                                label="Add Another Option"
                                                onClick={() => arrayHelpers.push('')}
                                                leftIcon={IconNames.ADD}
                                            />
                                        </div>
                                    )}
                                </FieldArray>
                            )}
                            {formik.values.component === ComponentTypes.RADIO && (
                                <>
                                    <DetailLabel label="Benefit Attribute Auto Population" />
                                    <FieldArray name="benefitAutoPopulate">
                                        {(arrayHelpers) => (
                                            <div className="margin__bottom--xx-large">
                                                {formik.values.benefitAutoPopulate.map(
                                                    (_condition, index) => (
                                                        <div
                                                            // eslint-disable-next-line react/no-array-index-key
                                                            key={index}
                                                            style={{ display: 'flex' }}
                                                        >
                                                            <div
                                                                className="padding__right--small"
                                                                style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns:
                                                                        '25px 30% 30px 30% 60px auto',
                                                                    flexGrow: '1',
                                                                }}
                                                            >
                                                                <div
                                                                    className="sds_question-logic__rule-text padding__right--small"
                                                                    style={{
                                                                        paddingTop:
                                                                            index === 0
                                                                                ? 26
                                                                                : undefined,
                                                                    }}
                                                                >
                                                                    If
                                                                </div>
                                                                <SuggestSingleSelectFormik
                                                                    id={`${arrayHelpers.name}.${index}.attribute_type`}
                                                                    label={
                                                                        index === 0
                                                                            ? 'Attribute Type'
                                                                            : undefined
                                                                    }
                                                                    values={
                                                                        benefitAttributeTypeOptions
                                                                    }
                                                                    isLoading={
                                                                        !benefitAttributeTypeOptions
                                                                    }
                                                                    required
                                                                />
                                                                <div
                                                                    className="sds_question-logic__rule-text padding__left--small padding__right--small"
                                                                    style={{
                                                                        paddingTop:
                                                                            index === 0
                                                                                ? 26
                                                                                : undefined,
                                                                    }}
                                                                >
                                                                    Is
                                                                </div>
                                                                <SuggestSingleSelectFormik
                                                                    id={`${arrayHelpers.name}.${index}.attribute_value`}
                                                                    label={
                                                                        index === 0
                                                                            ? 'Attribute Value'
                                                                            : undefined
                                                                    }
                                                                    required
                                                                    placeholderText="Enter value or select option"
                                                                    values={Object.values(
                                                                        BenefitAttributeAutoPopulateOptions,
                                                                    )}
                                                                    helperText={
                                                                        BenefitAttributeAutoPopulateOptionHelperTextMap[
                                                                            formik.values[
                                                                                arrayHelpers.name
                                                                            ][index].attribute_value
                                                                        ]
                                                                    }
                                                                    createNewItemFromQuery={(
                                                                        query,
                                                                    ) => query}
                                                                />
                                                                <div
                                                                    className="sds_question-logic__rule-text padding__left--small padding__right--small"
                                                                    style={{
                                                                        paddingTop:
                                                                            index === 0
                                                                                ? 26
                                                                                : undefined,
                                                                    }}
                                                                >
                                                                    Then
                                                                </div>
                                                                <SuggestSingleSelectFormik
                                                                    id={`${arrayHelpers.name}.${index}.option`}
                                                                    label={
                                                                        index === 0
                                                                            ? 'Auto Select Option'
                                                                            : undefined
                                                                    }
                                                                    required
                                                                    disableAutoPopulate
                                                                    values={
                                                                        formik.values.options?.filter(
                                                                            (option) => option,
                                                                        ) ?? []
                                                                    }
                                                                    validate={(value) =>
                                                                        !formik.values.options?.find(
                                                                            (option) =>
                                                                                option === value,
                                                                        )
                                                                            ? 'Must be a valid option'
                                                                            : undefined
                                                                    }
                                                                />
                                                            </div>
                                                            <Icon
                                                                className={
                                                                    index === 0
                                                                        ? 'margin__top--large'
                                                                        : 'margin__top--small'
                                                                }
                                                                title="Remove"
                                                                icon={IconNames.CROSS}
                                                                onClick={() =>
                                                                    arrayHelpers.remove(index)
                                                                }
                                                                style={{
                                                                    cursor: 'pointer',
                                                                }}
                                                            />
                                                        </div>
                                                    ),
                                                )}
                                                <ActionButtonText
                                                    label="Add Condition"
                                                    onClick={() => arrayHelpers.push('')}
                                                    leftIcon={IconNames.ADD}
                                                />
                                            </div>
                                        )}
                                    </FieldArray>
                                </>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

QuestionDialog.propTypes = {
    question: PropTypes.shape({
        question_id: PropTypes.number.isRequired,
        question_text: PropTypes.string,
        component: PropTypes.oneOf(Object.values(ComponentTypes)),
        options: PropTypes.arrayOf(PropTypes.shape({})),
        option_validation: PropTypes.arrayOf(PropTypes.shape({})),
        benefit_auto_populate: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    existingQuestionList: PropTypes.arrayOf(PropTypes.shape({})),
    payerId: PropTypes.string.isRequired,
    showAutoAccept: PropTypes.bool,
    isOpen: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSecondaryButtonClick: PropTypes.func.isRequired,
};
